import { CheckIcon, ChevronDownIcon, CloseIcon, InfoOutlineIcon } from "@chakra-ui/icons";
import {
    AspectRatio,
    Box,
    Button,
    Checkbox,
    Divider,
    Flex,
    IconButton,
    Image,
    Input,
    Menu,
    MenuButton,
    MenuItem,
    MenuList,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    Popover,
    PopoverArrow,
    PopoverBody,
    PopoverContent,
    PopoverTrigger,
    SimpleGrid,
    Spinner,
    Stack,
    Text,
    Textarea,
    Tooltip,
    useDisclosure,
    useToast
} from "@chakra-ui/react";
import SearchAdaccountAdspresso from "components/bmControl/SearchAdaccountAdspresso";
import { useFormik } from "formik";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { SketchPicker } from 'react-color';
import { BsStars } from "react-icons/bs";
import { FaPause, FaPlay } from "react-icons/fa";
import InfiniteScroll from 'react-infinite-scroll-component';
import { useDispatch } from "react-redux";
import { useGetBmAdAccountsCountQuery, useGetBmAdAccountsQuery } from "store/BmAccounts/bmAccountApi";
import { useGetVideoAvatarsQuery, useGetVideoVoicesQuery } from "store/aiHub/aiGenerationApi";
import { setAfter, setBefore, setCurrentPage } from "store/campaigns/campaignsSlice";
import {
    useFinishUploadMutation,
    useStartUploadSessionMutation, useUploadChunkMutation
} from 'store/driveUpload/uploadApi';
import { setFiles, setIsWidgetOpen } from "store/driveUpload/uploadSlice";
import { truncateName } from 'utils/functions';
import { adUplaodSchema } from "utils/schema";

const aspectRatioIcons = {
    "16:9": <Box w="16px" h="9px" borderColor="gray.100" border='1px' borderRadius="2px" />,
    "9:16": <Box w="9px" h="16px" borderColor="gray.100" border='1px' borderRadius="2px" />,
    "1:1": <Box w="12px" h="12px" borderColor="gray.100" border='1px' borderRadius="2px" />,
    "4.5": <Box w="14px" h="12px" borderColor="gray.100" border='1px' borderRadius="2px" />
};

const ALL_GENDERS = ["m", "f"];
const ALL_AGES = ["child", "adult"];
const ALL_LOCATIONS = ["indoor", "outdoor"];

function ColorPickerWithPopover({ color, onChange }) {
    return (
        <Popover>
            <PopoverTrigger>
                <Box
                    width="30px"
                    height="30px"
                    borderRadius="md"
                    bg={color}
                    border="1px solid"
                    borderColor="gray.300"
                    cursor="pointer"
                    _hover={{ borderColor: 'gray.500' }}
                />
            </PopoverTrigger>
            <PopoverContent width="fit-content" p={2}>
                <PopoverArrow />
                <PopoverBody>
                    <SketchPicker
                        color={color}
                        onChangeComplete={(c) => onChange(c.hex)}
                    />
                </PopoverBody>
            </PopoverContent>
        </Popover>
    );
}

const CustomAudioPlayer = ({
    id,
    src,
    currentAudio,
    setCurrentAudio,
    currentAudioId,
    setCurrentAudioId,
}) => {
    const audioRef = useRef(null);
    const isPlaying = currentAudioId === id;

    const togglePlay = () => {
        if (!audioRef.current) return;

        // If this is already playing, pause it
        if (isPlaying) {
            audioRef.current.pause();
            setCurrentAudio(null);
            setCurrentAudioId(null);
        } else {
            // Pause the current one
            if (currentAudio && currentAudio !== audioRef.current) {
                currentAudio.pause();
            }

            audioRef.current.play();
            setCurrentAudio(audioRef.current);
            setCurrentAudioId(id);
        }
    };

    useEffect(() => {
        const ref = audioRef.current;
        if (!ref) return;

        const handleEnded = () => {
            setCurrentAudio(null);
            setCurrentAudioId(null);
        };

        ref.addEventListener("ended", handleEnded);
        return () => ref.removeEventListener("ended", handleEnded);
    }, [setCurrentAudio, setCurrentAudioId]);

    return (
        <>
            <audio ref={audioRef} src={src} hidden />
            <IconButton
                color={'brand'}
                icon={isPlaying ? <FaPause /> : <FaPlay />}
                onClick={togglePlay}
                aria-label="Play/Pause"
                size="sm"
            />
        </>
    );
};

function HoverPreviewCard({ avatar, isSelected, onSelect }) {
    const [isHovered, setIsHovered] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const videoRef = useRef(null);

    useEffect(() => {
        if (videoRef.current) {
            if (isHovered) {
                videoRef.current.play().catch((err) => {
                    console.warn("Video playback failed:", err);
                });
            } else {
                videoRef.current.pause();
                videoRef.current.currentTime = 0;
            }
        }
    }, [isHovered]);

    return (
        <Box onClick={onSelect}>
            <AspectRatio ratio={1}>
                <Box
                    overflow="hidden"
                    borderRadius="8px"
                    border="1px solid"
                    borderColor={isSelected ? "brand" : "transparent"}
                    _hover={{ shadow: "md", cursor: "pointer", borderColor: "brand" }}
                    onMouseEnter={() => setIsHovered(true)}
                    onMouseLeave={() => setIsHovered(false)}
                    position="relative"
                >
                    {isSelected && (
                        <Box position="absolute" top="8px" left="8px" zIndex={3}>
                            <CheckIcon color="white" bg="brand" borderRadius="full" p="1" boxSize={5} />
                        </Box>
                    )}

                    {/* Image */}
                    <Image
                        src={avatar.preview_image_1_1}
                        alt={avatar.creator_name}
                        objectFit="cover"
                        position="absolute"
                        top={0}
                        left={0}
                        width="100%"
                        height="100%"
                        style={{
                            opacity: isHovered ? 0 : 1,
                            transition: "opacity 0.3s ease-in-out",
                        }}
                    />

                    {/* Spinner */}
                    {isHovered && isLoading && (
                        <Box
                            position="absolute"
                            top="50%"
                            left="50%"
                            transform="translate(-50%, -50%)"
                            zIndex={2}
                        >
                            <Spinner size="md" color="white" />
                        </Box>
                    )}

                    {/* Video */}
                    <video
                        ref={videoRef}
                        src={avatar.preview_video_1_1}
                        muted
                        loop
                        playsInline
                        onCanPlay={() => setIsLoading(false)}
                        onWaiting={() => setIsLoading(true)}
                        style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                            opacity: isHovered ? 1 : 0,
                            transition: "opacity 0.3s ease-in-out",
                        }}
                    />
                </Box>
            </AspectRatio>
            <Box mt={2}>
                <Text fontSize="xs" fontWeight="bold">
                    {avatar.creator_name}
                </Text>
            </Box>
        </Box>
    );
}

const UploadVideoAvatarModal = ({ isOpen, onClose, mediaType }) => {

    const toast = useToast();
    const dispatch = useDispatch();
    const { isOpen: isVoiceModalOpen, onOpen: onVoiceModalOpen, onClose: onVoiceModalClose } = useDisclosure();
    const [selectedAdAccounts, setSelectedAdAccounts] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [hasSearched, setHasSearched] = useState(false);
    const [startUploadSession] = useStartUploadSessionMutation();
    const [uploadChunk] = useUploadChunkMutation();
    const [finishUpload] = useFinishUploadMutation();
    const [afterValue, setAftervalue] = useState('');
    const [currentStep, setCurrentStep] = useState(0);
    const [selectedVoice, setSelectedVoice] = useState(null);
    const [selectedVoiceAccent, setSelectedVoiceAccent] = useState(null);
    const [page, setPage] = useState(1);
    const [avatars, setAvatars] = useState([]);
    const [currentAudio, setCurrentAudio] = useState(null);
    const [currentAudioId, setCurrentAudioId] = useState(null);
    const [hasMore, setHasMore] = useState(true);
    const [selectedAvatar, setSelectedAvatar] = useState(null);
    const [selectedGenders, setSelectedGenders] = useState([]);
    const [selectedAges, setSelectedAges] = useState([]);
    const [selectedLocations, setSelectedLocations] = useState([]);
    const [selectedAspectRatio, setSelectedAspectRatio] = useState("16:9");
    const [ctaText, setCtaText] = useState("");
    const [textColor, setTextColor] = useState("#fff");
    const [bgColor, setBgColor] = useState("red");

    const { data: accountsCount } = useGetBmAdAccountsCountQuery();
    const totalCount = accountsCount?.summary?.total_count ?? 20;
    const isSearching = !!searchTerm?.trim();
    const effectivePageSize = isSearching ? String(totalCount) : "20";

    const { data: AdAccountData, refetch: refetchAdaccountData, isLoading: isAdAccountLoading, isFetching: isAdAccountFetching } = useGetBmAdAccountsQuery(
        {
            search: searchTerm ? searchTerm : '',
            pageSize: effectivePageSize,
            after: searchTerm ? '' : afterValue,
        },
        { skip: !isOpen }
    );

    const filtersOnly = useMemo(() => {
        const f = {};

        if (selectedGenders.length > 0 && selectedGenders.length < ALL_GENDERS.length) {
            f.gender = selectedGenders.join(",");
        }

        if (selectedAges.length > 0 && selectedAges.length < ALL_AGES.length) {
            f.age_range = selectedAges.join(",");
        }

        if (selectedLocations.length > 0 && selectedLocations.length < ALL_LOCATIONS.length) {
            f.location = selectedLocations.join(",");
        }

        return f;
    }, [selectedGenders, selectedAges, selectedLocations]);

    useEffect(() => {
        setPage(1);
        setHasMore(true);
    }, [filtersOnly]);

    const videoAvatarFilters = useMemo(() => {
        const params = { ...filtersOnly };

        // Page logic: If filters are applied, always set page = 1
        if (Object.keys(filtersOnly).length > 0) {
            params.page = 1;
        } else {
            params.page = page;
        }

        params.page_size = 20;

        // console.log("Final API Params:", params);
        return params;
    }, [filtersOnly, page]);

    const shouldFetch = page > 0;

    const { data: videoAvatars, isLoading: isvideoAvatarsLoading, isFetching: isvideoAvatarsFetching } = useGetVideoAvatarsQuery(videoAvatarFilters, {
        skip: !shouldFetch,
    });
    const { data: videoVoices, isLoading: isvideoVoicesLoading } = useGetVideoVoicesQuery();;

    useEffect(() => {
        if (hasSearched && searchTerm === '') {
            refetchAdaccountData();
        }
    }, [searchTerm]);

    useEffect(() => {
        if (videoAvatars?.results) {
            setAvatars((prevAvatars) => {
                const newAvatars = videoAvatars.results.filter(
                    (a) => !prevAvatars.some((prev) => prev.id === a.id)
                );
                return page === 1 ? videoAvatars.results : [...prevAvatars, ...newAvatars];
            });
            setHasMore(Boolean(videoAvatars.next));
        }
    }, [videoAvatars]);

    const loadMoreAvatars = () => {
        if (hasMore && !isvideoAvatarsFetching) {
            setPage((prev) => prev + 1);
        }
    };

    // Reset on filter change
    useEffect(() => {
        setPage(1);
        setHasMore(true);
    }, [selectedGenders, selectedAges, selectedLocations]);

    const handleGenderChange = (gender) => {
        setSelectedGenders((prev) =>
            prev.includes(gender)
                ? prev.filter((g) => g !== gender)
                : [...prev, gender]
        );
    };

    const handleAgeChange = (age) => {
        setSelectedAges((prev) =>
            prev.includes(age) ? prev.filter((a) => a !== age) : [...prev, age]
        );
    };

    const handleLocationChange = (location) => {
        setSelectedLocations((prev) =>
            prev.includes(location)
                ? prev.filter((loc) => loc !== location)
                : [...prev, location]
        );
    };


    // Formik setup
    const uploadFormik = useFormik({
        initialValues: {
            adsAccountName: [],
            files: [],
        },
        validationSchema: adUplaodSchema,
        onSubmit: async (values, { setSubmitting }) => {
            // Handle form submission
            if (values) {
                handleUploadAll(values.files);
                dispatch(setIsWidgetOpen(true));
                dispatch(setFiles([]));
                dispatch(setAfter(''));
                dispatch(setBefore(''));
                dispatch(setCurrentPage(0));
                localStorage.removeItem('after');
                localStorage.removeItem('before');
            }
            setSubmitting(false);
            uploadFormik.resetForm();
            onClose();
            localStorage.removeItem('selectedAdspressoAdsAccount');
            setSelectedAdAccounts([]);
            setAftervalue('');
        },
    });

    const handleSelectVoice = (voice) => {
        setSelectedVoice(voice);
    };

    const handleAdaccountChange = (value) => {

        if (value) {
            // Check if the account is already selected
            const isAlreadySelected = selectedAdAccounts.some(
                (account) => account?.account_id === value?.account_id
            );
            if (isAlreadySelected) return;

            if (!isAlreadySelected) {
                // Prepare account details
                const accountDetails = {
                    ...value?.account,
                };

                // Add the account to the selectedAdAccounts state
                setSelectedAdAccounts((prevState) => {
                    const updatedAccounts = [accountDetails, ...prevState];

                    const accountNames = updatedAccounts.map(account => account.account_id);

                    uploadFormik.setFieldValue('adsAccountName', accountNames);
                    return updatedAccounts;
                });
            }
        }
    };

    const handleSearchChange = useCallback((value) => {
        setSearchTerm(value);
    }, []);

    // Function to remove an account from selectedAdAccounts
    const handleRemoveAdAccount = (accountId) => {
        // Remove the account from the selectedAdAccounts state
        setSelectedAdAccounts((prevState) => {
            const updatedAccounts = prevState.filter(account => account.account_id !== accountId);

            const accountNames = updatedAccounts.map(account => account.account_id);

            uploadFormik.setFieldValue('adsAccountName', accountNames);

            return updatedAccounts;
        });
    };


    const handleUploadAll = async (filesToUpload) => {
        // console.log((window.navigator.onLine ? 'on' : 'off') + 'line');

        window.addEventListener('online', () => {
            toast({
                title: "You are back online!",
                status: "success",
                duration: 4000,
                isClosable: true,
                position: "top-right",
            });
        });

        window.addEventListener('offline', () => {
            toast({
                title: "You are offline. Some functionality may be unavailable.",
                status: "error",
                duration: 4000,
                isClosable: true,
                position: "top-right",
            });
        });

        const uploadPromises = [];

        for (const file of filesToUpload) {
            const adsAccountsArray = uploadFormik?.values?.adsAccountName.map(accountId => {
                const selectedAdAccount = selectedAdAccounts?.find(account => account?.account_id === accountId);
                return selectedAdAccount ? {
                    id: selectedAdAccount?._id,
                    adsAccountId: selectedAdAccount?.account_id,
                    adsAccountName: selectedAdAccount?.name,
                    businessName: selectedAdAccount?.business?.name,
                    businessId: selectedAdAccount?.business?.id,
                } : null;
            }).filter(account => account !== null);
            if (adsAccountsArray.length === 0) {
                toast({
                    title: "Account Error",
                    description: "Please select a valid Ads Account.",
                    status: "error",
                    duration: 5000,
                    isClosable: true,
                    position: "top-right",
                });
                return;
            }

            if (file.type.startsWith("video/")) {

            }
        }

        // Wait for all uploads to complete
        await Promise.all(uploadPromises);
    };

    const handleNext = () => {
        setCurrentStep((prevStep) => prevStep + 1);
    };

    const handleBack = () => {
        setCurrentStep((prevStep) => prevStep - 1);
    };

    const showAdAccountError = !uploadFormik.values.adsAccountName.length;

    const handleModalClose = () => {
        uploadFormik.resetForm();
        dispatch(setFiles([]));
        onClose();
        localStorage.removeItem('selectedAdspressoAdsAccount');
        setSelectedAdAccounts([]);
        setAftervalue('');
        setCurrentStep(0);
    };

    const handleLoadMore = (after) => {
        setAftervalue(after);
    };

    const isFiltering =
        selectedGenders.length > 0 ||
        selectedAges.length > 0 ||
        selectedLocations.length > 0;


    return (
        <>
            <Modal isOpen={isVoiceModalOpen} onClose={onVoiceModalClose} size="4xl" scrollBehavior="inside">
                <ModalOverlay />
                <ModalContent minH="70vh">
                    <ModalHeader>Choose Voice</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody maxH={'60vh'} overflowY={'auto'}>
                        <SimpleGrid columns={[1, 2, 3]} spacing={4}>
                            {videoVoices?.flatMap((voice, voiceIndex) =>
                                voice.accents.map((accent, accentIndex) => {
                                    const key = `${voice.name}-${accent.accent_name}-${voiceIndex}-${accentIndex}`;

                                    return (
                                        <Box
                                            key={key}
                                            p={4}
                                            borderWidth="1px"
                                            borderRadius="md"
                                            borderColor={
                                                selectedVoice?.name === voice.name &&
                                                    selectedVoiceAccent?.id === accent.id
                                                    ? "orange.500"
                                                    : "gray.200"
                                            }
                                            _hover={{ bg: "gray.50", cursor: "pointer" }}
                                            onClick={() => {
                                                setSelectedVoice(voice);
                                                setSelectedVoiceAccent(accent);
                                            }}
                                            transition="background 0.2s"
                                            display="flex"
                                            flexDirection="row"
                                            alignItems="center"
                                            height="100%"
                                            gap={4}
                                        >
                                            {/* Audio Controls */}
                                            <Box>
                                                {accent.preview_url && (
                                                    <CustomAudioPlayer
                                                        id={key}
                                                        src={accent.preview_url}
                                                        currentAudio={currentAudio}
                                                        setCurrentAudio={setCurrentAudio}
                                                        currentAudioId={currentAudioId}
                                                        setCurrentAudioId={setCurrentAudioId}
                                                    />
                                                )}
                                            </Box>

                                            {/* Voice Info */}
                                            <Flex direction="column" gap={1}>
                                                <Text fontWeight="bold" fontSize="14px">
                                                    {truncateName(voice.name, 17)}
                                                </Text>
                                                <Text fontSize="12px" mb={1}>
                                                    {voice.gender
                                                        ? voice.gender.charAt(0).toUpperCase() + voice.gender.slice(1)
                                                        : "Gender"}
                                                    {accent.accent_name ? `, ${truncateName(accent.accent_name)}` : ""}
                                                </Text>
                                            </Flex>
                                        </Box>
                                    );
                                })
                            )}
                        </SimpleGrid>
                    </ModalBody>
                    <ModalFooter>
                        <Button
                            size='sm'
                            colorScheme='brand'
                            isDisabled={!selectedVoice}
                            onClick={() => {
                                handleSelectVoice(selectedVoice);
                                onVoiceModalClose();
                            }}
                        >
                            Use this voice
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>


            <Modal isOpen={isOpen} onClose={() => handleModalClose()} size={{ base: "6xl" }} scrollBehavior="inside" closeOnOverlayClick={false} closeOnEsc={false}>
                <ModalOverlay />
                <ModalContent minH="80vh">
                    <ModalHeader>
                        <ModalCloseButton />
                        <Flex gap={2} direction={'column'}>
                            <Flex alignItems={'center'} gap={2}>
                                <BsStars />
                                <Text>AI Video Generation</Text>
                                <Tooltip
                                    borderRadius={5}
                                    label={
                                        <Flex direction={'column'} gap={2} fontSize="12px" p="5px">
                                            <Text fontWeight={'bold'}>Generated with AI</Text>
                                            <Text>AI-generated image variations are inspired by your original asset. Adding multiple image options to your ad can help tailor creatives to different audiences, reduce creative fatigue, and improve overall performance.</Text>
                                            <Flex direction={'column'} gap={2}>
                                                <Text fontWeight={'bold'}>Types of AI Variations:</Text>
                                                <Text>Background Variation: Your product is placed in different, relevant environments.</Text>
                                                <Text>Creative Variation: Entirely new visuals generated based on your original creative.</Text>
                                            </Flex>
                                            <Text>Note: Hustle does not guarantee the completeness, accuracy, or reliability of AI-generated videos.</Text>
                                        </Flex>
                                    }
                                    fontSize="md"
                                >
                                    <InfoOutlineIcon pointerEvents="all" />
                                </Tooltip>
                            </Flex>
                        </Flex>
                    </ModalHeader>
                    <Divider mb={'10px'} />
                    <ModalBody>

                        <Flex direction={'row'} minH={'60vh'}>

                            {currentStep === 0 && (
                                <>
                                    <Box flex={'40%'} maxH="500px" p={2}>

                                        {/* Display chosen accounts */}
                                        <Text mb={2} fontWeight={"bold"}>
                                            Choose Ad Accounts
                                        </Text>
                                        <Text fontSize={'12px'} mb={2}>
                                            The AI-generated image will be saved only to the My Drive of the Ad Accounts you select below
                                        </Text>

                                        <SearchAdaccountAdspresso
                                            onInitialisation={() => { }}
                                            accountOptions={AdAccountData?.data}
                                            onAccountChange={handleAdaccountChange}
                                            onSearchInputChange={handleSearchChange}
                                            onSearchInputTouched={setHasSearched}
                                            AdsAccountIdFromParams={''}
                                            isAdAccountLoading={isAdAccountLoading}
                                            isAdAccountFetching={isAdAccountFetching}
                                            pageMeta={AdAccountData?.meta}
                                            onLoadMore={handleLoadMore}
                                            selectedAccounts={selectedAdAccounts}
                                            onRemoveAccount={handleRemoveAdAccount}
                                            isForStatusUpload={true}
                                        />
                                    </Box>

                                    <Box flex={'2%'} maxH="500px">
                                        <Divider orientation='vertical' />
                                    </Box>

                                    <Box flex={'58%'} maxH="500px" p={2} overflowY="auto">
                                        <Text mb={2} fontWeight={"bold"}>
                                            Selected Ad Accounts
                                        </Text>

                                        <Stack spacing={1} mt={2}>
                                            {selectedAdAccounts?.map((account, index) => (
                                                <Flex key={`${account?.account_id}-${index}`} align="center" direction="row">
                                                    {/* Account name and account ID with currency on separate lines */}
                                                    <Flex direction="column" align="flex-start" flex={0.9} p={1}>
                                                        <Text fontSize="12px">
                                                            {account?.name?.length > 48
                                                                ? `${account.name.substring(0, 48)}...`
                                                                : account?.name}
                                                        </Text>
                                                        <Flex gap={"5px"}>
                                                            <Text fontSize="11px" color="gray.500">
                                                                {account?.account_id}
                                                            </Text>
                                                        </Flex>
                                                    </Flex>

                                                    {/* Remove button */}
                                                    <Flex justify="flex-end" flex={0.1}>
                                                        <Tooltip label="Remove" aria-label="Remove account tooltip" fontSize={'12px'}>
                                                            <IconButton
                                                                icon={<CloseIcon />}
                                                                size="sm"
                                                                variant="ghost"
                                                                colorScheme="red"
                                                                onClick={() => handleRemoveAdAccount(account.account_id)}
                                                                aria-label="Remove account"
                                                            />
                                                        </Tooltip>
                                                    </Flex>
                                                </Flex>
                                            ))}
                                        </Stack>
                                    </Box>
                                </>
                            )}

                            {currentStep === 1 && (
                                <>
                                    <Box flex={'33%'} maxH="500px" >
                                        <Flex direction={'column'} pr={'10px'}>
                                            <Flex alignItems={'center'} gap={2} mb={2}>
                                                <Text fontWeight={"bold"} fontSize={'md'}>
                                                    Enter Script
                                                </Text>
                                                <Tooltip
                                                    borderRadius={5}
                                                    label={
                                                        <Flex direction={'column'} gap={2} fontSize="sm" p="5px">

                                                        </Flex>
                                                    }
                                                    fontSize="md"
                                                >
                                                    <InfoOutlineIcon pointerEvents="all" />
                                                </Tooltip>
                                            </Flex>
                                            <Flex direction={'column'} gap={2}>
                                                <Flex direction="column" gap={4}>
                                                    <Textarea
                                                        placeholder="Enter the script, choose avatar, and generate your video in minutes!"
                                                        bg="#fff"
                                                        size="sm"
                                                        borderRadius="md"
                                                        height="200px"
                                                        resize="vertical"
                                                    />
                                                    <Menu>
                                                        <MenuButton
                                                            as={Button}
                                                            bg="none"
                                                            border="1px solid"
                                                            borderColor="gray.100"
                                                            _hover={{ bg: 'gray.50' }}
                                                            _active={{ bg: 'none' }}
                                                            size="sm"
                                                            borderRadius="md"
                                                            onClick={onVoiceModalOpen}
                                                            px={4}
                                                        >
                                                            <Flex width="full" justifyContent="space-between" alignItems="center">
                                                                <Box>{selectedVoice ? selectedVoice?.name : "Select a Voice"}</Box>
                                                                <ChevronDownIcon boxSize={6} />
                                                            </Flex>
                                                        </MenuButton>
                                                    </Menu>

                                                    <Flex bg='gray.50' alignItems={'center'} justifyContent={'space-between'} p={2} borderRadius="md">
                                                        <Text fontWeight={'bold'}>Aspect ratio</Text>
                                                        <Menu closeOnSelect={false}>
                                                            <MenuButton as={Button} width={'105px'} bg='none' border='1px solid' borderColor='gray.100' rightIcon={<ChevronDownIcon boxSize={4} />} size="sm">
                                                                <Flex align="center" gap={2}>
                                                                    {aspectRatioIcons[selectedAspectRatio]}
                                                                    <Box fontSize="12px">{selectedAspectRatio}</Box>
                                                                </Flex>
                                                            </MenuButton>
                                                            <MenuList p={2} borderRadius="8px" mt={-1} w="100px" minW="unset">
                                                                {["9:16", "16:9", "1:1", "4.5"].map((ratio) => (
                                                                    <MenuItem
                                                                        fontSize="12px"
                                                                        key={ratio}
                                                                        borderRadius="5px"
                                                                        bg={selectedAspectRatio === ratio ? "gray.100" : "transparent"}
                                                                        fontWeight={selectedAspectRatio === ratio ? "bold" : "normal"}
                                                                        onClick={() => setSelectedAspectRatio(ratio)}
                                                                        icon={aspectRatioIcons[ratio]}
                                                                    >
                                                                        {ratio}
                                                                    </MenuItem>
                                                                ))}
                                                            </MenuList>
                                                        </Menu>
                                                    </Flex>

                                                    <Flex direction="column" gap={2} maxW="400px" p={4} bg="gray.50" borderRadius="md">
                                                        <Text fontWeight="bold" fontSize="sm">
                                                            Enter your CTA text
                                                        </Text>

                                                        <Input
                                                            placeholder="Type here"
                                                            size="sm"
                                                            borderRadius="md"
                                                            value={ctaText}
                                                            onChange={(e) => setCtaText(e.target.value)}
                                                        />
                                                        <Flex justifyContent={'space-between'}>
                                                            <Flex alignItems={'center'} gap={2}>
                                                                <Text fontWeight="bold" fontSize="sm" mb={1}>
                                                                    Text Color
                                                                </Text>
                                                                <ColorPickerWithPopover color={textColor} onChange={setTextColor} />
                                                            </Flex>

                                                            <Flex alignItems={'center'} gap={2}>
                                                                <Text fontWeight="bold" fontSize="sm" mb={1}>
                                                                    Background Color
                                                                </Text>
                                                                <ColorPickerWithPopover color={bgColor} onChange={setBgColor} />
                                                            </Flex>
                                                        </Flex>

                                                    </Flex>
                                                </Flex>
                                            </Flex>
                                        </Flex>
                                    </Box>

                                    <Box flex={'2%'} maxH="500px">
                                        <Divider orientation='vertical' />
                                    </Box>

                                    <Box flex="65%" maxH="500px" display="flex" flexDirection="column">
                                        {/* Fixed Header & Filters */}
                                        <Flex direction="column" gap={2} flexShrink={0}>
                                            <Flex>
                                                <Text fontWeight="bold" fontSize="md">Choose Avatar</Text>
                                            </Flex>

                                            <Flex mb={2} gap={2}>
                                                {/* Gender Menu */}
                                                <Menu closeOnSelect={false}>
                                                    <MenuButton as={Button} colorScheme='brand' _expanded={{ bg: 'orange.600' }} rightIcon={<ChevronDownIcon />} size="xs">
                                                        Gender
                                                    </MenuButton>
                                                    <MenuList p={2} borderRadius="8px" mt={-1}>
                                                        <MenuItem closeOnSelect={false} borderRadius="5px" _hover={{ backgroundColor: "gray.50" }}>
                                                            <Checkbox
                                                                size="sm"
                                                                colorScheme='brand'
                                                                isChecked={selectedGenders.includes("m")}
                                                                onChange={() => handleGenderChange("m")}
                                                            >
                                                                <Text fontSize="12px">Male</Text>
                                                            </Checkbox>
                                                        </MenuItem>
                                                        <MenuItem closeOnSelect={false} borderRadius="5px" _hover={{ backgroundColor: "gray.50" }}>
                                                            <Checkbox
                                                                size="sm"
                                                                colorScheme='brand'
                                                                isChecked={selectedGenders.includes("f")}
                                                                onChange={() => handleGenderChange("f")}
                                                            >
                                                                <Text fontSize="12px">Female</Text>
                                                            </Checkbox>
                                                        </MenuItem>
                                                    </MenuList>
                                                </Menu>

                                                {/* Age Menu */}
                                                <Menu closeOnSelect={false}>
                                                    <MenuButton as={Button} colorScheme='brand' _expanded={{ bg: 'orange.600' }} rightIcon={<ChevronDownIcon />} size="xs">
                                                        Age
                                                    </MenuButton>
                                                    <MenuList p={2} borderRadius="8px" mt={-1}>
                                                        <MenuItem closeOnSelect={false} borderRadius="5px" _hover={{ backgroundColor: "gray.50" }}>
                                                            <Checkbox
                                                                size="sm"
                                                                colorScheme='brand'
                                                                isChecked={selectedAges.includes("adult")}
                                                                onChange={() => handleAgeChange("adult")}
                                                            >
                                                                <Text fontSize="12px">Adult</Text>
                                                            </Checkbox>
                                                        </MenuItem>
                                                        <MenuItem closeOnSelect={false} borderRadius="5px" _hover={{ backgroundColor: "gray.50" }}>
                                                            <Checkbox
                                                                size="sm"
                                                                colorScheme='brand'
                                                                isChecked={selectedAges.includes("senior")}
                                                                onChange={() => handleAgeChange("senior")}
                                                            >
                                                                <Text fontSize="12px">Senior</Text>
                                                            </Checkbox>
                                                        </MenuItem>
                                                    </MenuList>
                                                </Menu>

                                                {/* Location Menu */}
                                                <Menu closeOnSelect={false}>
                                                    <MenuButton as={Button} colorScheme='brand' _expanded={{ bg: 'orange.600' }} rightIcon={<ChevronDownIcon />} size="xs">
                                                        Location
                                                    </MenuButton>
                                                    <MenuList p={2} borderRadius="8px" mt={-1}>
                                                        <MenuItem closeOnSelect={false} borderRadius="5px" _hover={{ backgroundColor: "gray.50" }}>
                                                            <Checkbox
                                                                size="sm"
                                                                colorScheme='brand'
                                                                isChecked={selectedLocations.includes("indoor")}
                                                                onChange={() => handleLocationChange("indoor")}
                                                            >
                                                                <Text fontSize="12px">Indoor</Text>
                                                            </Checkbox>
                                                        </MenuItem>
                                                        <MenuItem closeOnSelect={false} borderRadius="5px" _hover={{ backgroundColor: "gray.50" }}>
                                                            <Checkbox
                                                                size="sm"
                                                                colorScheme='brand'
                                                                isChecked={selectedLocations.includes("outdoor")}
                                                                onChange={() => handleLocationChange("outdoor")}
                                                            >
                                                                <Text fontSize="12px">Outdoor</Text>
                                                            </Checkbox>
                                                        </MenuItem>
                                                    </MenuList>
                                                </Menu>

                                                {(selectedGenders.length > 0 || selectedAges.length > 0 || selectedLocations.length > 0) && (
                                                    <Button
                                                        size="xs"
                                                        colorScheme='brand'
                                                        variant="outline"
                                                        onClick={() => {
                                                            setSelectedGenders([]);
                                                            setSelectedAges([]);
                                                            setSelectedLocations([]);
                                                            setPage(1);
                                                            setHasMore(true);
                                                        }}
                                                        ml={2}
                                                    >
                                                        Clear all ({selectedGenders.length + selectedAges.length + selectedLocations.length})
                                                    </Button>
                                                )}

                                            </Flex>
                                        </Flex>

                                        {/* Scrollable Grid Container */}
                                        <Box id="scrollableDiv" flex="1" overflowY="auto" mt={2} height="70vh">
                                            {(avatars.length === 0 && isvideoAvatarsLoading) || (isFiltering && isvideoAvatarsFetching) ? (
                                                <Flex alignItems="center" justifyContent="center" h="70vh">
                                                    <Spinner size="md" />
                                                </Flex>
                                            ) : (
                                                <InfiniteScroll
                                                    dataLength={avatars?.length}
                                                    next={loadMoreAvatars}
                                                    hasMore={hasMore}
                                                    loader={
                                                        <Flex alignItems="center" justifyContent="center" py={4}>
                                                            <Spinner size="md" />
                                                        </Flex>
                                                    }
                                                    scrollableTarget="scrollableDiv"
                                                >
                                                    <SimpleGrid columns={[1, 2, 3, 4]} spacing={4}>
                                                        {avatars.map((avatar) => {
                                                            const isSelected = selectedAvatar?.id === avatar.id;

                                                            return (
                                                                <HoverPreviewCard
                                                                    key={avatar.id}
                                                                    avatar={avatar}
                                                                    isSelected={isSelected}
                                                                    onSelect={() => {
                                                                        if (isSelected) {
                                                                            setSelectedAvatar(null); // Deselect if already selected
                                                                        } else {
                                                                            setSelectedAvatar(avatar); // Select if not selected
                                                                        }
                                                                    }}
                                                                />
                                                            );
                                                        })}
                                                    </SimpleGrid>
                                                </InfiniteScroll>
                                            )}
                                        </Box>
                                    </Box>

                                </>
                            )}

                            {currentStep === 2 && (
                                <Text>Genearated output</Text>
                            )}
                        </Flex>
                    </ModalBody>
                    <ModalFooter>
                        <Flex width="100%" justifyContent="space-between" alignItems="center">
                            {/* Left Side */}
                            {currentStep === 1 ? (
                                <Flex alignItems="center" gap="8px">
                                    <InfoOutlineIcon fontSize="12px" mb={4} />
                                    <Text fontSize="12px">
                                        AI-generated videos will be stored only in the "My Drive" of the designated Ad Accounts..
                                    </Text>
                                </Flex>
                            ) :
                                <Flex>
                                </Flex>
                            }

                            {/* Right Side: Back or Next Button */}
                            <Flex gap={2}>
                                {currentStep === 0 && (
                                    <Flex>
                                        <Button size="sm" colorScheme='brand' onClick={handleNext} isDisabled={showAdAccountError}>
                                            Next
                                        </Button>
                                    </Flex>
                                )}
                                {currentStep === 1 && (
                                    <Flex gap={2}>
                                        <Button 
                                            colorScheme="gray"
                                            variant="solid"
                                            size="sm" 
                                            onClick={handleBack}
                                        >
                                            Back
                                        </Button>
                                        <Button
                                            colorScheme='brand'
                                            size="sm"
                                            isDisabled={!selectedAvatar}
                                            onClick={handleNext}
                                        >
                                            Next
                                        </Button>
                                    </Flex>
                                )}
                                {currentStep === 2 && (
                                    <Flex gap={2}>
                                        <Button 
                                            colorScheme="gray"
                                            variant="solid"
                                            size="sm" 
                                            onClick={handleBack}
                                        >
                                            Back
                                        </Button>
                                        <Button
                                            colorScheme='brand'
                                            size="sm"
                                            onClick={uploadFormik.handleSubmit}
                                            isDisabled={showAdAccountError || !uploadFormik.isValid}
                                        >
                                            Save
                                        </Button>
                                    </Flex>
                                )}
                            </Flex>
                        </Flex>
                    </ModalFooter>
                </ModalContent>
            </Modal >
        </>
    );
};

export default UploadVideoAvatarModal;
